import React, { useMemo, useState } from 'react';
import { BookOpen, Search, X, Sparkles, Coins, Scale, ScrollText, Shield, Landmark, ChevronDown } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

/**
 * Knowledge Centre — Amanah by Zakat.International V2
 *
 * Premium editorial redesign (Feb 2026):
 *  - Deep emerald canvas with a faint gold geometric watermark
 *  - Category chips filter (All · Zakat · Faraid · Wasiyyah · Privacy)
 *  - Pill-shaped premium search
 *  - 24px-radius ivory cards with a champagne gold accent bar on the left
 *  - Bigger question typography, generous whitespace
 *  - Pull-quote style citation footer per answer
 *  - Custom expand/collapse (no Radix accordion — simpler, more elegant)
 *
 * Content stays word-identical to the FAQPage JSON-LD in /public/index.html
 * so Google rich-results + AI engines cross-reference cleanly.
 */
/**
 * FAQ dataset — Q&A bodies come from translations (v2.faq.qa.<id>.q / .a).
 * The `id`, `cat` and optional `cite` fields stay hard-coded here since
 * they are language-agnostic (citations are proper nouns like
 * "Sahih al-Bukhari 2742"). English text lives in translationsV2Extra.en.
 */
export const FAQ_DATA = [
  { id: 'how-zakat',           cat: 'zakat' },
  { id: 'faraid-calc',         cat: 'faraid',   cite: 'Quran 4:11, 4:12, 4:176' },
  { id: 'sunni-schools',       cat: 'faraid' },
  { id: 'wasiyyah-limit',      cat: 'wasiyyah', cite: 'Sahih al-Bukhari 2742 \u00B7 Sahih Muslim 1628 \u00B7 Sunan al-Tirmidhi 2120' },
  { id: 'nisab',               cat: 'zakat' },
  { id: 'data-storage',        cat: 'privacy' },
  { id: 'deterministic-vs-ai', cat: 'faraid' },
  { id: 'languages',           cat: 'privacy' },
  { id: 'deduction-order',     cat: 'faraid' },
  { id: 'over-third',          cat: 'wasiyyah', cite: 'Sahih al-Bukhari 2742' },
];

const CATEGORIES = [
  { id: 'all',      tkey: 'v2.faq.cat.all',      Icon: Sparkles },
  { id: 'zakat',    tkey: 'v2.faq.cat.zakat',    Icon: Coins },
  { id: 'faraid',   tkey: 'v2.faq.cat.faraid',   Icon: Scale },
  { id: 'wasiyyah', tkey: 'v2.faq.cat.wasiyyah', Icon: ScrollText },
  { id: 'privacy',  tkey: 'v2.faq.cat.privacy',  Icon: Shield },
];

const FAQItem = ({ item, index, open, onToggle, testid }) => {
  const num = String(index + 1).padStart(2, '0');
  return (
    <div
      data-testid={`${testid}-item-${item.id}`}
      className={`group relative overflow-hidden rounded-[24px] bg-white border transition-all duration-300 ${
        open
          ? 'border-gold-500/50 shadow-[0_10px_40px_-12px_rgba(201,164,75,0.35)]'
          : 'border-stone-200/80 hover:border-gold-500/35 hover:shadow-[0_6px_24px_-12px_rgba(13,45,34,0.18)]'
      }`}
    >
      {/* Champagne gold accent bar */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-[3px] transition-colors ${
          open ? 'bg-gold-500' : 'bg-gold-500/25 group-hover:bg-gold-500/60'
        }`}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`${testid}-q-${item.id}`}
        className="w-full text-left px-6 md:px-7 py-5 md:py-6 flex items-start gap-4"
      >
        <span
          aria-hidden="true"
          className={`shrink-0 mt-0.5 w-8 h-8 rounded-full text-[11px] font-mono flex items-center justify-center transition-colors ${
            open
              ? 'bg-[#EDF5F1] text-[#0D4733] ring-1 ring-gold-500/60'
              : 'bg-[#EDF5F1] text-[#0D4733] ring-1 ring-gold-500/35'
          }`}
        >
          {num}
        </span>
        <span className="flex-1 min-w-0">
          <h3 className="faq-question font-heading text-[17px] md:text-[19px] leading-snug text-[#0D1520]">
            {item.q}
          </h3>
        </span>        <ChevronDown
          className={`shrink-0 w-5 h-5 mt-1 text-[#4A5568] transition-transform duration-300 ${
            open ? 'rotate-180 text-gold-600' : ''
          }`}
        />
      </button>

      {/* Answer — smooth grid-row animation */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div
            data-testid={`${testid}-a-${item.id}`}
            className="px-6 md:px-7 pb-6 md:pb-7 pl-[76px] md:pl-[80px]"
          >
            <p className="text-[15px] md:text-[16px] leading-[1.75] text-stone-700">
              {item.a}
            </p>
            {item.cite && (
              <div className="mt-4 pt-3 border-t border-gold-500/25 flex items-center gap-2 text-[12px] text-[#4A5568] italic">
                <BookOpen className="w-3.5 h-3.5 text-gold-600 shrink-0" />
                <span>{item.cite}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FAQSection = ({ testid = 'faq-section', defaultOpen = 'how-zakat' }) => {
  const { t } = useZakatGlobal();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [openId, setOpenId] = useState(defaultOpen);

  // Resolve translations up-front so search-scoring and rendering both work
  // against the currently-selected language. `resolvedFAQ` is a plain array
  // of full-string items ready for filter/render.
  const resolvedFAQ = useMemo(
    () => FAQ_DATA.map((f) => ({
      ...f,
      q: t(`v2.faq.qa.${f.id}.q`),
      a: t(`v2.faq.qa.${f.id}.a`),
    })),
    [t]
  );

  // Token-based scoring across question + answer + citation.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const catFiltered = category === 'all'
      ? resolvedFAQ
      : resolvedFAQ.filter((f) => f.cat === category);
    if (!q) return catFiltered.map((f, i) => ({ ...f, _i: i, _score: 0 }));
    const tokens = q.split(/\s+/).filter(Boolean);
    return catFiltered
      .map((f, i) => {
        const hay = `${f.q} ${f.a} ${f.cite || ''}`.toLowerCase();
        const score = tokens.reduce((acc, tok) => acc + (hay.includes(tok) ? 1 : 0), 0);
        return { ...f, _i: i, _score: score };
      })
      .filter((f) => f._score > 0)
      .sort((a, b) => b._score - a._score);
  }, [query, category, resolvedFAQ]);

  const showEmpty = (query.trim() || category !== 'all') && results.length === 0;

  return (
    <section
      id="faq"
      className="relative overflow-hidden py-14 md:py-20"
      data-testid={testid}
      style={{
        background:
          'radial-gradient(1100px 700px at 22% 12%, rgba(201,164,75,0.10), transparent 55%),' +
          'radial-gradient(700px 500px at 88% 90%, rgba(13,45,34,0.06), transparent 60%),' +
          'linear-gradient(180deg, #FAF7F2 0%, #F1EDE4 100%)',
      }}
    >
      {/* Faint gold geometric watermark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'><g fill='none' stroke='%230D2D22' stroke-width='0.5'><polygon points='60,6 78,30 108,30 90,54 108,78 78,78 60,102 42,78 12,78 30,54 12,30 42,30'/><polygon points='60,30 69,42 84,42 75,54 84,66 69,66 60,78 51,66 36,66 45,54 36,42 51,42'/><circle cx='60' cy='54' r='22'/></g></svg>\")",
          backgroundSize: '180px 180px',
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial heading */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-[0.22em] text-[#0D1520] border border-[#EDE5D6] bg-[#F3EFEA] backdrop-blur">
            <Landmark className="w-3 h-3 text-gold-600" />
            {t('v2.faq.kicker')}
          </div>
          <h2 className="mt-4 font-heading text-[28px] sm:text-[36px] md:text-[42px] leading-[1.1] tracking-tight text-[#0D1520]">
            {t('v2.faq.title.a')} <span className="italic text-gold-600">{t('v2.faq.title.b')}</span>.
          </h2>
          <p className="mt-3 text-[15px] text-stone-600 max-w-xl mx-auto leading-relaxed">
            {t('v2.faq.subtitle')}
          </p>
        </div>

        {/* Pill-shaped premium search */}
        <div className="mb-5" data-testid={`${testid}-search-wrap`}>
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0D4733] pointer-events-none" />
            <input
              id="faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('v2.faq.search')}
              data-testid={`${testid}-search`}
              className="w-full h-14 pl-12 pr-12 rounded-full bg-[#F3EFEA] border border-stone-200 shadow-[0_4px_20px_-8px_rgba(13,45,34,0.15)] focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25 text-[15px] text-[#0D1520] placeholder:text-stone-400 transition-shadow"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                data-testid={`${testid}-search-clear`}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-stone-500 hover:text-[#0D1520] hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Category chips */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10"
          data-testid={`${testid}-categories`}
        >
          {CATEGORIES.map(({ id, tkey, Icon }) => {
            const active = category === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setCategory(id)}
                data-testid={`${testid}-cat-${id}`}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12.5px] font-medium transition-all ${
                  active
                    ? 'bg-[#EDF5F1] text-[#0D4733] border border-gold-500/50 shadow-[0_2px_10px_-2px_rgba(13,45,34,0.35)]'
                    : 'bg-[#F3EFEA] backdrop-blur text-[#0D1520] border border-stone-200 hover:border-gold-500/40 hover:bg-[#FAF8F5]'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${active ? 'text-gold-400' : 'text-gold-600'}`} />
                {t(tkey)}
              </button>
            );
          })}
        </div>

        {/* Result count line */}
        {(query.trim() || category !== 'all') && !showEmpty && (
          <p
            className="mb-4 text-[11px] font-mono uppercase tracking-widest text-[#4A5568] text-center"
            data-testid={`${testid}-count`}
          >
            {results.length} {results.length === 1 ? t('v2.faq.count.answer') : t('v2.faq.count.answers')}
          </p>
        )}

        {showEmpty ? (
          <div
            data-testid={`${testid}-empty`}
            className="rounded-[24px] bg-[#F3EFEA] border border-stone-200 p-8 text-center"
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-gold-500/15 ring-1 ring-gold-500/40 flex items-center justify-center mb-3">
              <Search className="w-5 h-5 text-gold-600" />
            </div>
            <p className="font-heading text-[19px] text-[#0D1520]">
              {t('v2.faq.empty.title')}
            </p>
            <p className="mt-2 text-[14px] text-stone-600 leading-relaxed max-w-md mx-auto">
              {t('v2.faq.empty.body')}
            </p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4" data-testid={`${testid}-list`}>
            {results.map((item, idx) => (
              <FAQItem
                key={item.id}
                item={item}
                index={item._i ?? idx}
                open={openId === item.id}
                onToggle={() => setOpenId((cur) => (cur === item.id ? null : item.id))}
                testid={testid}
              />
            ))}
          </div>
        )}

        {/* Footer note */}
        <p className="mt-10 text-center text-[12px] text-stone-500 italic max-w-lg mx-auto leading-relaxed">
          {t('v2.faq.footer')}
        </p>
      </div>
    </section>
  );
};

export default FAQSection;
